/**
 * Page back-office : validation des demandes d'inscription parents.
 *
 * Workflow : le parent envoie une demande depuis l'app, ici on vérifie son
 * identité au téléphone puis on clique « Générer le code » → on voit un code
 * 6 chiffres à dicter au parent au téléphone.
 */
import React, { useState } from 'react';
import {
  ClipboardCheck, Phone, Mail, KeyRound, X, Copy, Check, AlertCircle, Loader2,
} from 'lucide-react';
import {
  useGetInscriptionsQuery,
  useGenererCodeInscriptionMutation,
  useRejeterInscriptionMutation,
} from '../../services/inscriptionsParentsApi';
import type { DemandeInscription } from '../../services/inscriptionsParentsApi';

const STATUTS = [
  { v: 'en_attente',  label: 'En attente',     couleur: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  { v: 'code_envoye', label: 'Code dicté',     couleur: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { v: 'valide',      label: 'Validée',        couleur: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  { v: 'rejete',      label: 'Rejetée',        couleur: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
];

export default function InscriptionsParents() {
  const [filtre, setFiltre] = useState<string>('en_attente');
  const [modal, setModal] = useState<{ type: 'code' | 'rejet'; demande: DemandeInscription } | null>(null);

  const { data, isFetching, refetch } = useGetInscriptionsQuery({ statut: filtre });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ClipboardCheck size={24} /> Inscriptions Parents
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Demandes d'inscription en attente de validation. Appelez le parent pour vérifier
          son identité, puis générez son code à 6 chiffres.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {STATUTS.map(s => {
          const count = filtre === s.v ? (data?.results.length ?? 0)
            : (s.v === 'en_attente' ? data?.nb_en_attente
               : s.v === 'code_envoye' ? data?.nb_code_envoye : undefined);
          return (
            <button key={s.v}
              onClick={() => setFiltre(s.v)}
              className={`px-3 py-1.5 text-xs rounded ${
                filtre === s.v
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}>
              {s.label}{count !== undefined && count > 0 && ` (${count})`}
            </button>
          );
        })}
        <button onClick={() => setFiltre('all')}
          className={`px-3 py-1.5 text-xs rounded ${
            filtre === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}>
          Toutes
        </button>
        <button onClick={() => refetch()} className="ml-auto px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
          {isFetching ? '…' : '↻ Actualiser'}
        </button>
      </div>

      {!isFetching && data?.results.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 text-center">
          <ClipboardCheck size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400 dark:text-gray-600" />
          <div className="text-gray-600 dark:text-gray-400 text-sm">Aucune demande dans cette catégorie.</div>
        </div>
      )}

      <div className="space-y-3">
        {data?.results.map(d => {
          const meta = STATUTS.find(s => s.v === d.statut);
          return (
            <div key={d.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                    {(d.prenoms?.[0] || '') + (d.nom?.[0] || '')}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {d.prenoms} {d.nom}
                      {d.auto_validee && (
                        <span className="ml-2 text-[10px] bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 px-2 py-0.5 rounded">
                          Auto-validée
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Demandé le {new Date(d.date_demande).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                {meta && <span className={`text-[10px] px-2 py-0.5 rounded ${meta.couleur}`}>{meta.label}</span>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-3">
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Parent</div>
                  <div className="text-sm space-y-1">
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <Phone size={12} /> <a href={`tel:${d.telephone}`} className="text-blue-500 hover:underline">{d.telephone}</a>
                    </div>
                    {d.email && (
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Mail size={12} /> {d.email}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Relation : {{
                        pere: 'Père', mere: 'Mère', tuteur: 'Tuteur', autre: 'Autre',
                      }[d.relation] || d.relation}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-3">
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Enfant déclaré</div>
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {d.prenoms_etudiant} {d.nom_etudiant}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {d.classe_etudiant || '—'} • <span className="font-mono">{d.matricule_etudiant}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Né le {new Date(d.date_naissance_etudiant).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>

              {d.statut === 'code_envoye' && d.code_validation && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded p-3 mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-semibold text-blue-700 dark:text-blue-300 uppercase mb-1">Code à dicter</div>
                    <div className="text-2xl font-bold tracking-widest text-blue-700 dark:text-blue-200">{d.code_validation}</div>
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400">
                    Le parent doit le saisir dans l'app.
                  </div>
                </div>
              )}

              {d.statut === 'rejete' && d.motif_rejet && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded p-3 mb-3">
                  <div className="text-xs text-red-700 dark:text-red-300">
                    <strong>Motif du rejet :</strong> {d.motif_rejet}
                  </div>
                  <div className="text-[10px] text-red-600 dark:text-red-400 mt-1">
                    par {d.traite_par_nom}
                  </div>
                </div>
              )}

              {(d.statut === 'en_attente' || d.statut === 'code_envoye') && (
                <div className="flex gap-2">
                  <button onClick={() => setModal({ type: 'code', demande: d })}
                    className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center justify-center gap-2">
                    <KeyRound size={13} />
                    {d.statut === 'code_envoye' ? 'Régénérer un code' : 'Valider (générer le code)'}
                  </button>
                  <button onClick={() => setModal({ type: 'rejet', demande: d })}
                    className="px-3 py-2 text-sm bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded inline-flex items-center gap-1">
                    <X size={13} /> Rejeter
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modal?.type === 'code' && (
        <ModalCode demande={modal.demande} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'rejet' && (
        <ModalRejet demande={modal.demande} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function ModalCode({ demande, onClose }: { demande: DemandeInscription; onClose: () => void }) {
  const [generer, { isLoading, data, error }] = useGenererCodeInscriptionMutation();
  const [copie, setCopie] = useState(false);

  React.useEffect(() => {
    generer(demande.id);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [demande.id]);

  const code = data?.code || '';

  const copierCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 dark:text-white">Code de validation</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </header>

        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Vous êtes en ligne avec <strong>{demande.prenoms} {demande.nom}</strong> au{' '}
            <a href={`tel:${demande.telephone}`} className="text-blue-500">{demande.telephone}</a> ?
            Vérifiez son identité et l'identité de son enfant ({demande.prenoms_etudiant} {demande.nom_etudiant},{' '}
            {demande.matricule_etudiant}), puis dictez-lui ce code :
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={28} className="animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded p-3 text-sm">
              Erreur lors de la génération du code.
            </div>
          ) : (
            <div className="text-center">
              <div className="inline-block bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-300 dark:border-blue-700 rounded-lg px-8 py-6 mb-3">
                <div className="text-5xl font-black tracking-widest text-blue-700 dark:text-blue-300">{code}</div>
              </div>
              <button onClick={copierCode}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
                {copie ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier</>}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">
                Le parent doit saisir ce code dans son app dans les 24h.
                Vous pouvez fermer cette fenêtre.
              </p>
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">
            J'ai dicté le code
          </button>
        </footer>
      </div>
    </div>
  );
}

function ModalRejet({ demande, onClose }: { demande: DemandeInscription; onClose: () => void }) {
  const [motif, setMotif] = useState('');
  const [rejeter, { isLoading }] = useRejeterInscriptionMutation();

  const handle = async () => {
    if (!motif.trim()) return;
    try {
      await rejeter({ id: demande.id, motif }).unwrap();
      onClose();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-bold text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertCircle size={18} /> Rejeter la demande
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </header>

        <div className="p-5 space-y-3">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Vous rejetez la demande de <strong>{demande.prenoms} {demande.nom}</strong>{' '}
            pour {demande.matricule_etudiant}.
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motif du rejet (visible par le parent)
            </label>
            <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} maxLength={500}
              placeholder="Ex. Numéro de téléphone ne correspondant pas à un parent connu."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded">
            Annuler
          </button>
          <button onClick={handle} disabled={isLoading || !motif.trim()}
            className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded">
            Confirmer le rejet
          </button>
        </footer>
      </div>
    </div>
  );
}
